import React, { useState } from 'react';
import { Briefcase, CheckCircle2, XCircle, Check } from 'lucide-react';
import { StepNavButtons } from './StepNavButtons';
import { MarkdownContent } from '../MarkdownContent';
import type { Case, ModuleStep, StepResultResponse } from '@/lib/models/module';

interface CaseStepRendererProps {
    step: ModuleStep;
    caseData?: Case;
    onSubmit: (answer: any) => Promise<StepResultResponse>;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

export const CaseStepRenderer: React.FC<CaseStepRendererProps> = ({ step, caseData, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const [answers, setAnswers] = useState<Record<number, any>>({});
    const [result, setResult] = useState<StepResultResponse | null>(null);

    if (!caseData) {
        return (
            <div className="p-6 text-center text-gray-500">
                Caset kunde inte laddas.
            </div>
        );
    }

    const questions: any[] = Array.isArray(caseData.questions) ? caseData.questions : [];

    const setAnswer = (qIdx: number, value: any) => {
        setAnswers((prev) => ({ ...prev, [qIdx]: value }));
    };

    const allAnswered = questions.every((_, idx) => answers[idx] !== undefined && answers[idx] !== '');

    const handleSubmit = async () => {
        const answerMap: Record<string, any> = {};
        for (let i = 0; i < questions.length; i++) {
            answerMap[String(i)] = answers[i] ?? '';
        }
        const res = await onSubmit({ answers: answerMap });
        setResult(res);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#A4C3B2] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-[#1A4D4F]" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">{caseData.case_title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {caseData.focus_area} &middot; {caseData.difficulty_level}
                    </p>
                </div>
            </div>

            {/* Case description */}
            <div className="p-4 rounded-xl bg-gray-50 border-2 border-gray-200">
                <MarkdownContent>{caseData.case_description}</MarkdownContent>
            </div>

            {/* Result banner */}
            {result && (
                <div className={`p-4 rounded-xl ${result.correct ? 'bg-green-50 border-2 border-green-200' : 'bg-amber-50 border-2 border-amber-200'}`}>
                    <div className="flex items-center gap-2">
                        {result.correct
                            ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                            : <XCircle className="w-5 h-5 text-amber-600" />
                        }
                        <span className="font-semibold text-gray-900">
                            {result.correct ? 'Bra jobbat!' : 'Kontrollera dina svar'}
                        </span>
                    </div>
                    {result.ai_feedback && (
                        <div className="mt-3 space-y-2 text-sm text-gray-700">
                            <p>{result.ai_feedback.assessment}</p>
                            {result.ai_feedback.strengths?.length > 0 && (
                                <div>
                                    <span className="font-medium text-green-700">Styrkor: </span>
                                    {result.ai_feedback.strengths.join(', ')}
                                </div>
                            )}
                            {result.ai_feedback.improvements?.length > 0 && (
                                <div>
                                    <span className="font-medium text-amber-700">Förbättringsområden: </span>
                                    {result.ai_feedback.improvements.join(', ')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
                {questions.map((q: any, idx: number) => (
                    <QuestionRenderer
                        key={idx}
                        index={idx}
                        question={q}
                        answer={answers[idx]}
                        onAnswer={(val) => setAnswer(idx, val)}
                        disabled={!!result?.correct}
                        isWrong={result?.wrong_questions?.includes(idx) ?? false}
                    />
                ))}
            </div>

            {/* Submit / Continue */}
            {result?.step_completed ? (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={() => onSubmit({ _advance: true })}
                    nextLabel="Fortsätt"
                />
            ) : (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={handleSubmit}
                    nextLabel={isSubmitting ? 'Skickar...' : 'Skicka in svar'}
                    nextDisabled={!allAnswered || isSubmitting}
                />
            )}
        </div>
    );
};

// ── Per-question renderer ────────────────────────────────────────

interface QuestionRendererProps {
    index: number;
    question: any;
    answer: any;
    onAnswer: (value: any) => void;
    disabled: boolean;
    isWrong: boolean;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({ index, question, answer, onAnswer, disabled, isWrong }) => {
    const type = question.question_type || 'text_input';

    return (
        <div className={`p-4 sm:p-5 rounded-2xl border transition-colors ${isWrong ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
            {/* Question — numbered badge + markdown body */}
            <div className="mb-4 flex items-start gap-2.5">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#1A4D4F]/10 text-xs font-bold text-[#1A4D4F]">
                    {index + 1}
                </span>
                <div className="flex-1">
                    <MarkdownContent className="[&_p]:m-0 [&_p]:font-semibold [&_p]:text-gray-900">
                        {question.question_text}
                    </MarkdownContent>
                </div>
            </div>

            {type === 'multiple_choice' && (
                <div className="space-y-2">
                    {question.options?.map((opt: any) => {
                        const isSelected = answer === opt.option_id;
                        return (
                            <button
                                key={opt.option_id}
                                disabled={disabled}
                                onClick={() => onAnswer(opt.option_id)}
                                className={`group flex w-full items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-all disabled:cursor-default ${
                                    isSelected
                                        ? 'border-[#1A4D4F] bg-[#A4C3B2]/20 shadow-sm'
                                        : 'border-gray-200 hover:border-[#1A4D4F]/40 hover:bg-gray-50'
                                }`}
                            >
                                <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                                        isSelected ? 'border-[#1A4D4F] bg-[#1A4D4F]' : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                                    }`}
                                >
                                    {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                                </span>
                                <span className="text-sm font-medium text-gray-900">{opt.text}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {type === 'multi_select' && (
                <div className="space-y-2">
                    {question.options?.map((opt: any) => {
                        const selected: string[] = Array.isArray(answer) ? answer : [];
                        const isChecked = selected.includes(opt.option_id);
                        return (
                            <label
                                key={opt.option_id}
                                className={`group flex items-center gap-3 rounded-xl border-2 p-3.5 transition-all ${disabled ? 'cursor-default' : 'cursor-pointer'} ${
                                    isChecked ? 'border-[#1A4D4F] bg-[#A4C3B2]/20 shadow-sm' : 'border-gray-200 hover:border-[#1A4D4F]/40 hover:bg-gray-50'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    disabled={disabled}
                                    checked={isChecked}
                                    onChange={() => {
                                        const next = isChecked
                                            ? selected.filter((id) => id !== opt.option_id)
                                            : [...selected, opt.option_id];
                                        onAnswer(next);
                                    }}
                                    className="sr-only"
                                />
                                <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                                        isChecked ? 'border-[#1A4D4F] bg-[#1A4D4F]' : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                                    }`}
                                >
                                    {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                </span>
                                <span className="text-sm font-medium text-gray-900">{opt.text}</span>
                            </label>
                        );
                    })}
                </div>
            )}

            {(type === 'text_input' || type === 'ai_evaluated') && (
                <textarea
                    disabled={disabled}
                    value={answer || ''}
                    onChange={(e) => onAnswer(e.target.value)}
                    placeholder="Skriv ditt svar här..."
                    rows={4}
                    className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-[#1A4D4F] focus:ring-1 focus:ring-[#1A4D4F] outline-none resize-none text-sm"
                />
            )}

            {type === 'confirmation' && (
                <label className="group flex items-center gap-3 rounded-xl border-2 border-gray-200 p-3.5 cursor-pointer transition-all hover:border-[#1A4D4F]/40 hover:bg-gray-50">
                    <input
                        type="checkbox"
                        disabled={disabled}
                        checked={!!answer}
                        onChange={(e) => onAnswer(e.target.checked)}
                        className="sr-only"
                    />
                    <span
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                            answer ? 'border-[#1A4D4F] bg-[#1A4D4F]' : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                        }`}
                    >
                        {!!answer && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{question.checkbox_label || 'Jag bekräftar'}</span>
                </label>
            )}
        </div>
    );
};
